/** Utility functions for toggling task completion, including recurring task logic. */
import { generateKeyBetween } from 'fractional-indexing';
import { addDays, addWeeks, addMonths } from 'date-fns';
import {
  getNewTaskRef,
  getLastSiblingTask,
  getTask,
} from '@/services/taskService';
import {
  getTaskBatch,
  commitBatch,
  updateTaskInBatch,
  setTaskInBatch,
} from '@/services/taskMutations';
import { newTaskSchema } from '@/features/tasks/utils/data/taskSchema';
import { shouldAdjustDateTime } from '@/features/tasks/utils/mutations/taskMutationUtils';
import { calculateStartTimeFromNow } from '@/utils/timeUtils';
import { MS_PER_MINUTE } from '@/config/constants';
import { logTaskCompleted } from '@/services/analyticsService';

export const applyFocusModeUpdates = (
  mainTaskPayload,
  task,
  activeFocusTaskId,
  focusStartTime,
  stopFocus,
) => {
  if (
    !mainTaskPayload.completed ||
    task.id !== activeFocusTaskId ||
    !focusStartTime
  )
    return;
  const elapsedMs = Date.now() - focusStartTime;
  const elapsedMinutes = Math.max(5, Math.round(elapsedMs / MS_PER_MINUTE));
  mainTaskPayload.duration = elapsedMinutes;
  mainTaskPayload.measuredDuration = elapsedMinutes;
  mainTaskPayload.estimatedDuration = task.duration ?? 0;
  if (stopFocus) stopFocus();
};

export const applyAutoScheduling = (mainTaskPayload, task) => {
  if (!mainTaskPayload.completed) return;
  if (!shouldAdjustDateTime(task)) return;
  const { date, time } = calculateStartTimeFromNow(
    mainTaskPayload.duration ?? task.duration,
  );
  mainTaskPayload.date = date;
  mainTaskPayload.time = time;
};

export const buildMainTaskPayload = (
  task,
  validatedData,
  activeFocusTaskId,
  focusStartTime,
  stopFocus,
) => {
  const mainTaskPayload = {
    ...validatedData,
    completedAt: validatedData.completed ? new Date() : null,
  };
  applyFocusModeUpdates(
    mainTaskPayload,
    task,
    activeFocusTaskId,
    focusStartTime,
    stopFocus,
  );
  applyAutoScheduling(mainTaskPayload, task);
  return mainTaskPayload;
};

export const updateDescendantsIfCompleted = (
  batch,
  validatedData,
  task,
  taskTree,
  getAllDescendantIds,
) => {
  if (!validatedData.completed) return;
  const descendantIds = getAllDescendantIds(task.id, taskTree);
  const descendantPayload = {
    completed: true,
    completedAt: new Date(),
  };
  for (const descendantId of descendantIds) {
    updateTaskInBatch(batch, descendantId, descendantPayload);
  }
};

const getLastSiblingOrderKey = async (parentId, userId) => {
  const lastTask = await getLastSiblingTask(parentId, userId);
  return generateKeyBetween(lastTask?.orderKey, undefined);
};

const getParentMaterializedPath = async (parentId) => {
  if (!parentId) return '';
  const p = await getTask(parentId);
  return p?.materializedPath || '';
};

export const calculateRecurrenceDate = (task) => {
  const now = new Date();
  const { interval, frequency } = task.recurrence;
  if (frequency === 'days') return addDays(now, interval);
  if (frequency === 'weeks') return addWeeks(now, interval);
  if (frequency === 'months') return addMonths(now, interval);
  return now;
};

export const buildRecurringTaskData = (
  task,
  newOrderKey,
  newMaterializedPath,
  userId,
) => {
  const newTaskData = { ...task };
  delete newTaskData.id;
  delete newTaskData.completedAt;
  delete newTaskData.updatedAt;
  delete newTaskData.createdAt;

  newTaskData.completed = false;
  newTaskData.parentId = task.parentId || null;
  newTaskData.rescheduleCount = 0;
  newTaskData.requireTimeReview = false;

  const newDeadline = calculateRecurrenceDate(task);
  newTaskData.date = newDeadline.toISOString().split('T')[0];
  newTaskData.time = null;
  newTaskData.orderKey = newOrderKey;
  newTaskData.materializedPath = newMaterializedPath;
  newTaskData.userId = userId;

  return newTaskData;
};

const createRecurringTaskIfNeeded = async (batch, task, userId) => {
  if (!task.recurrence) return;

  const parentId = task.parentId || null;
  const newOrderKey = await getLastSiblingOrderKey(parentId, userId);
  const parentMaterializedPath = await getParentMaterializedPath(parentId);
  const newMaterializedPath = `${parentMaterializedPath}${newOrderKey}.`;

  const newTaskData = buildRecurringTaskData(
    task,
    newOrderKey,
    newMaterializedPath,
    userId,
  );
  const validationResult = newTaskSchema.safeParse(newTaskData);

  if (!validationResult.success) {
    console.error(
      'Validation failed for recurring task:',
      validationResult.error.flatten(),
    );
    return;
  }

  const newTaskRef = getNewTaskRef();
  setTaskInBatch(batch, newTaskRef, validationResult.data);
};

export const executeToggleComplete = async (
  task,
  validatedData,
  tasksContext,
  focusContext,
  userId,
) => {
  const { taskTree, getAllDescendantIds } = tasksContext;
  const { activeFocusTaskId, focusStartTime, stopFocus } = focusContext;
  const batch = getTaskBatch();

  const mainTaskPayload = buildMainTaskPayload(
    task,
    validatedData,
    activeFocusTaskId,
    focusStartTime,
    stopFocus,
  );
  updateTaskInBatch(batch, task.id, mainTaskPayload);

  updateDescendantsIfCompleted(
    batch,
    validatedData,
    task,
    taskTree,
    getAllDescendantIds,
  );
  await createRecurringTaskIfNeeded(batch, task, userId);

  try {
    await commitBatch(batch);
    if (validatedData.completed) {
      logTaskCompleted();
    }
  } catch (err) {
    console.error(
      '[taskToggleUtils] Critical: Batch commit failed for toggle complete:',
      err,
    );
    throw err;
  }
};
